import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CloseButton, Combobox, Loader, TextInput, useCombobox } from '@mantine/core';

import { ServiceConstants } from "constant";
import { useListHook } from "hooks";
import { AppUtils } from "utils";
import { AutocompleteProps } from "types";

import { IRootState } from "config/redux";
import { useTranslation } from "react-i18next";

const { REQUEST_PENDING } = ServiceConstants;

let searchTimeout: NodeJS.Timeout;

interface AsyncAutocompleteProps extends AutocompleteProps {
  getData: (search: string | undefined) => void;
  serviceObjectId: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  paramMessageKey?: string | undefined;
  pageable: boolean;
  readOnly?: boolean;
  reloadOnChange?: any[];
  renderName?: (item: any) => string;
}

const AsyncAutocomplete = (props: AsyncAutocompleteProps) => {
  const { t } = useTranslation();
  const { serviceObjectId, getData } = props;
  const serviceObject = useSelector((state: IRootState) => state.service.object[serviceObjectId]);
  const { content, updateList, updatePageable, resetList } = useListHook();
  const [refetch, setRefetch] = useState<number>(0);
  const search = useRef<string | undefined>(props.value?.name);
  const firstTime = useRef<boolean>(true);

  // Reset field after clear
  useEffect(() => {
    const name = props.value?.name;
    if (!name) {
      resetField();
    }
  }, [props.value?.name]);

  useEffect(() => {
    return () => {
      search.current = undefined;
      clearTimeout(searchTimeout);
      resetList();
      setRefetch(0);
    }
  }, []);

  useEffect(() => {
    if (props.paramMessageKey !== undefined || !refetch) {
      resetList();
      return;
    }
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      resetList();
      getData(search.current);
    }, firstTime.current ? 0 : 500);
  }, [refetch, props.paramMessageKey]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, serviceObjectId, (val) => {
      resetList();
      if (props.pageable) {
        updatePageable(val);
      } else {
        updateList(val);
      }
    })
  }, [serviceObject]);

  const combobox = useCombobox({
    onDropdownOpen: () => setRefetch(old => old + 1),
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const isLoading = useCallback(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);
  const getName = useCallback((item: any) => props.renderName ? props.renderName(item) : item.name, [props.renderName])
  const resetField = useCallback(() => {
    props.onChange(undefined);
    search.current = undefined;
    if (combobox.searchRef.current) {
      combobox.searchRef.current.value = '';
    }
    combobox.resetSelectedOption();
  }, []);

  return (
    <Combobox
      onOptionSubmit={(value, optionProps) => {
        props.onChange(value ? {
          id: value,
          name: optionProps.children
        } : undefined);
        search.current = optionProps.children as string;
        combobox.closeDropdown();
        combobox.searchRef.current?.blur();
      }}
      withinPortal={true}
      store={combobox}
    >
      <Combobox.Target>
        <TextInput
          ref={combobox.searchRef}
          label={props.label && t(props.label)}
          placeholder={props.placeholder && t(props.placeholder)}
          value={search.current ?? props.value?.name ?? ''}
          onChange={(event) => {
            firstTime.current = false;
            search.current = event.currentTarget.value;
            setRefetch(old => old + 1);
            combobox.resetSelectedOption();
            combobox.openDropdown();
          }}
          onFocus={() => {
            firstTime.current = true;
            combobox.openDropdown()
            if (search.current === undefined) {
              search.current = '';
              setRefetch(old => old + 1);
            }
          }}
          onClick={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={
            isLoading() ? <Loader size={18} /> :
            (!props.readOnly && !props.disabled)  && props.value ? (
              <CloseButton
                size="sm"
                onMouseDown={(event) => event.preventDefault()}
                onClick={resetField}
              />
            ) : undefined
          }
          required={props.required}
          withAsterisk={props.required}
          readOnly={props.readOnly || props.disabled}
          disabled={props.readOnly || props.disabled}
          error={props.error}
        />
      </Combobox.Target>
      <Combobox.Dropdown hidden={!content && !props.paramMessageKey}>
        <Combobox.Options>
          {content && content.length > 0 && content.map((item) => (
            <Combobox.Option value={item.id} key={item.id}>{getName(item)}</Combobox.Option>
          ))}
          {content && content.length === 0 && <Combobox.Empty>{t('common.results.no')}</Combobox.Empty>}
          {props.paramMessageKey && <Combobox.Empty>{t(props.paramMessageKey)}</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export default AsyncAutocomplete;